import type { ReactNode } from 'react';
import { Logo } from './Logo';
import type { ConnectionState } from '../../lib/useLiveSync';
import type { SessionUser } from '../../lib/types';
import { ROLE_LABELS } from '../../lib/labels';
import './shell.css';

export type TabId =
  | 'vorschau'
  | 'schichtplan'
  | 'verfuegbarkeiten'
  | 'mitarbeiter'
  | 'baustellen'
  | 'einstellungen';

const TABS: { id: TabId; label: string; adminOnly?: boolean }[] = [
  { id: 'vorschau', label: 'Vorschau' },
  { id: 'schichtplan', label: 'Schichtplan' },
  { id: 'verfuegbarkeiten', label: 'Verfügbarkeiten' },
  { id: 'mitarbeiter', label: 'Mitarbeiter' },
  { id: 'baustellen', label: 'Baustellen' },
  { id: 'einstellungen', label: 'Einstellungen' },
];

interface AppShellProps {
  user: SessionUser;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  connection: ConnectionState;
  onLogout: () => void;
  children: ReactNode;
}

export function AppShell({
  user,
  activeTab,
  onTabChange,
  connection,
  onLogout,
  children,
}: AppShellProps) {
  return (
    <div className="app">
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>

      <header className="topbar no-print">
        <div className="topbar-brand">
          <Logo height={22} />
          <span className="topbar-app">Schichtplanung</span>
        </div>

        <div className="spacer" />

        <ConnectionBadge state={connection} />

        <div className="topbar-user">
          <span className="topbar-user-name">{user.displayName}</span>
          <span className="topbar-user-role">{ROLE_LABELS[user.role]}</span>
        </div>

        <button type="button" className="btn btn-ghost btn-small" onClick={onLogout}>
          Abmelden
        </button>
      </header>

      <nav className="tabbar no-print" aria-label="Bereiche">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className="tab"
            aria-current={activeTab === tab.id ? 'page' : undefined}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="app-main" id="inhalt">
        {children}
      </main>
    </div>
  );
}

/**
 * Zeigt an, ob Änderungen der Kollegen gerade ankommen. Bei einem Werkzeug,
 * an dem mehrere gleichzeitig arbeiten, muss ein stiller Verbindungsabbruch
 * sichtbar sein — sonst plant jemand auf veraltetem Stand.
 */
function ConnectionBadge({ state }: { state: ConnectionState }) {
  const label =
    state === 'verbunden'
      ? 'Live verbunden'
      : state === 'verbindet'
        ? 'Verbindung wird aufgebaut'
        : 'Keine Live-Verbindung';

  return (
    <span className={`conn conn-${state}`} title={label}>
      <span className="conn-dot" aria-hidden="true" />
      <span className="conn-label">{state === 'verbunden' ? 'Live' : label}</span>
    </span>
  );
}
